import * as React from 'react';
import { fadeIn } from 'react-animations';
import { Modal, ModalBody } from 'reactstrap';
import styled, { keyframes } from 'styled-components';
import { device } from '../../../utils/constants';

const bounceAnimation = keyframes`${fadeIn}`;

export const Container = styled.div`
  width: 50vw;
  height: 26vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${(props) => (props.hasVideo ? 'pointer' : 'default')};

  background-image: url(${(props) => props.img});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 0% 100%;

  @media ${device.mobile} {
    width: 100vw;
    height: 56vw;
  }
`;

export const BodyInfo = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  background-color: rgba(0, 0, 0, 0.4);
  align-items: center;
  justify-content: center;
  position: absolute;

  animation: 0.5s ${bounceAnimation};
`;
export const Info = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h3 {
    font-weight: normal;
    font-size: 1.8em;
    text-align: center;
    font-family: MontserratBold;
  }
`;

export const Links = styled.h2`
  cursor: pointer;
  text-align: center;
  font-family: MontserratMedium;
`;

const PublicidadComponent = ({ img, title, desc, url }) => {
  const [isHover, setIsHover] = React.useState(false);
  const [isModal, setIsModal] = React.useState(false);
  const [error, setError] = React.useState(false);

  // Función para convertir URLs de video a formato embed (iframe)
  const getEmbedUrl = (videoUrl) => {
    if (!videoUrl) return null;

    // Limpiar espacios en blanco
    videoUrl = videoUrl.trim();

    // Si ya es un link de embed o player, retornarlo
    if (
      videoUrl.includes('player.vimeo.com') ||
      videoUrl.includes('youtube.com/embed') ||
      videoUrl.includes('media.publit.io')
    ) {
      return videoUrl;
    }

    // Convertir YouTube youtu.be a formato embed
    if (videoUrl.includes('youtu.be/')) {
      const videoId = videoUrl
        .split('youtu.be/')[1]
        ?.split('?')[0]
        ?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Convertir YouTube watch links a formato embed
    if (videoUrl.includes('youtube.com/watch')) {
      const videoId = videoUrl.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Convertir YouTube shorts a formato embed
    if (videoUrl.includes('youtube.com/shorts/')) {
      const videoId = videoUrl.split('shorts/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }

    // Convertir Vimeo links regulares a formato player
    if (
      videoUrl.includes('vimeo.com/') &&
      !videoUrl.includes('player.vimeo.com')
    ) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        // Preservar parámetros adicionales si existen (como ?h=...)
        const params = videoUrl.includes('?') ? videoUrl.split('?')[1] : '';
        return params
          ? `https://player.vimeo.com/video/${videoId}?${params}`
          : `https://player.vimeo.com/video/${videoId}`;
      }
    }

    // Si es un link directo (publit.io u otros), retornarlo tal cual
    return videoUrl;
  };

  const embedUrl = React.useMemo(() => getEmbedUrl(url), [url]);

  // Función para validar si hay un URL de video válido
  const hasValidVideo = React.useMemo(() => {
    if (!url) return false;
    if (url === '') return false;
    if (url === undefined) return false;
    if (url === null) return false;
    if (url === '-') return false;
    return !!embedUrl;
  }, [url, embedUrl]);

  const toggle = () => {
    if (!hasValidVideo) return;

    setIsModal(!isModal);
    setError(false);
  };

  const handleIframeError = () => {
    setError(true);
    console.error('Error al cargar el video:', embedUrl);
  };

  const info = <BodyInfo></BodyInfo>;

  return (
    <Container
      onClick={toggle}
      onMouseEnter={() => {
        if (hasValidVideo) {
          setIsHover(true);
        }
      }}
      onMouseLeave={() => {
        setIsHover(false);
      }}
      img={img}
      hasVideo={hasValidVideo}
    >
      {isHover && info}

      <Modal isOpen={isModal} toggle={toggle} centered={true} size="lg">
        <ModalBody style={{ backgroundColor: 'black', padding: '20px' }}>
          {error ? (
            <div
              style={{
                color: 'white',
                textAlign: 'center',
                padding: '40px',
              }}
            >
              <p>Error al cargar el video.</p>
              <p
                style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.8 }}
              >
                URL: {embedUrl || url}
              </p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#007bff',
                  textDecoration: 'underline',
                  marginTop: '20px',
                  display: 'inline-block',
                }}
              >
                Abrir en nueva pestaña
              </a>
            </div>
          ) : (
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
              }}
            >
              <iframe
                src={embedUrl}
                title="Video"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onError={handleIframeError}
              />
            </div>
          )}
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PublicidadComponent;
